import { anchoNecesarioMm, code128, PUNTOS_MINIMOS, trazar } from './code128';

/**
 * Etiquetas de pallet: la que se pega al bajar un pallet del contenedor.
 *
 * El pallet llega de China con su propia etiqueta —material, cantidad, PO—,
 * pero no con un código que nuestro escáner pueda leer. Esta es la que se
 * imprime acá para poder operarlo: sólo el código de barras del ID, el
 * artículo bien grande (es lo que un operario usa para distinguir un pallet
 * de otro a simple vista) y el ID chico debajo, por si el escáner falla.
 *
 * El rollo es fijo —100×150 mm— y entran dos pallets por hoja, arriba y
 * abajo, separados por una línea de corte punteada. A diferencia de
 * `etiquetasUbicacion.ts` no hay tamaño ni orientación para elegir: es una
 * sola medida y un solo diseño.
 */

export type PalletEtiqueta = {
  id: string;
  /** El artículo con más piezas, o null si el pallet no tiene ninguna línea cargada. */
  articulo: string | null;
  /** Cuántos artículos distintos tiene arriba. Más de uno se muestra «<primero> +N». */
  articulos: number;
  /** De qué contenedor bajó (su código). */
  contenedor?: string | null;
  /**
   * El número de llegada de ese contenedor: es lo que va en la etiqueta,
   * «Contenedor N° 12», porque así lo nombra la gente en la descarga. Sin
   * número todavía, se pone el código.
   */
  contenedorNumero?: number | null;
};

const ANCHO = 100;
const ALTO = 150;
/** Lo que mide cada mitad de la hoja, arriba y abajo. */
const ALTO_MITAD = ALTO / 2;
const MARGEN_X = Math.max(3, ANCHO * 0.07);
const ANCHO_DISPONIBLE = ANCHO - MARGEN_X * 2;

const FUENTE = 'Geist, Helvetica, Arial, sans-serif';
const FUENTE_MONO = "'Geist Mono', ui-monospace, monospace";

const escapar = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Agrupa la lista de a dos para las hojas de 100×150. La última queda sola si el total es impar. */
export function enPares(lista: PalletEtiqueta[]): [PalletEtiqueta, PalletEtiqueta | null][] {
  const pares: [PalletEtiqueta, PalletEtiqueta | null][] = [];
  for (let i = 0; i < lista.length; i += 2) pares.push([lista[i]!, lista[i + 1] ?? null]);
  return pares;
}

/** «A123 +1» si el pallet tiene más de un artículo arriba; si no, el artículo solo. */
function textoProducto(p: PalletEtiqueta): string {
  if (!p.articulo) return 'Sin artículo';
  return p.articulos > 1 ? `${p.articulo} +${p.articulos - 1}` : p.articulo;
}

/**
 * El tamaño de fuente más grande que entra: a lo ancho (según cuántos
 * caracteres hay, con el mismo factor de 0,62 em por carácter que ya usa
 * `etiquetasUbicacion.ts`) y a lo alto del renglón que se le dio.
 */
const fuenteQueEntra = (texto: string, altoDisponible: number, minimo: number) =>
  Math.max(minimo, Math.min((ANCHO_DISPONIBLE * 0.92) / (Math.max(1, texto.length) * 0.62), altoDisponible * 0.8));

/** Si el código de barras del ID entra en la hoja sin bajar del módulo mínimo. */
export function codigoCabe(id: string): boolean {
  const codigo = code128(id);
  return codigo !== null && anchoNecesarioMm(codigo, PUNTOS_MINIMOS) <= ANCHO_DISPONIBLE;
}

/** El código de barras de esa mitad, o el aviso de que no entra —igual que en `etiquetasUbicacion.ts`. */
function barras(id: string, offsetY: number, barY: number, barAlto: number): string {
  const codigo = code128(id);
  if (codigo !== null && codigoCabe(id)) {
    return trazar(codigo, ANCHO_DISPONIBLE)
      .barras.map(
        (b) =>
          `<rect x="${(MARGEN_X + b.x).toFixed(3)}" y="${(offsetY + barY).toFixed(3)}" width="${b.ancho.toFixed(3)}" height="${barAlto.toFixed(3)}" fill="#000"/>`,
      )
      .join('');
  }
  const mensaje = codigo === null ? 'código no válido' : 'código no entra en esta medida';
  return (
    `<text x="${ANCHO / 2}" y="${(offsetY + barY + barAlto / 2).toFixed(3)}" font-size="${Math.max(2.5, ANCHO * 0.045).toFixed(2)}" ` +
    `text-anchor="middle" fill="#000">${mensaje}</text>`
  );
}

/** Una de las dos mitades de la hoja: el código, el artículo grande y, chicos, el ID y el contenedor. */
function mitad(p: PalletEtiqueta, offsetY: number): string {
  // 6 mm hasta el corte: si la tijera se va un poco, no se lleva barras.
  const barY = ALTO_MITAD * 0.08;
  const barAlto = ALTO_MITAD * 0.34;

  const textoProd = escapar(textoProducto(p));
  const fuenteProd = fuenteQueEntra(textoProd, ALTO_MITAD * 0.22, 4);
  const yProd = offsetY + ALTO_MITAD * (p.contenedor || p.contenedorNumero != null ? 0.66 : 0.72);

  // El contenedor, en su renglón y más grande que el ID: es lo que se busca
  // con la vista al separar los pallets de una descarga.
  const textoCont =
    p.contenedorNumero != null
      ? `Contenedor N° ${p.contenedorNumero}`
      : p.contenedor
        ? escapar(`Contenedor ${p.contenedor}`)
        : '';
  const fuenteCont = fuenteQueEntra(textoCont, ALTO_MITAD * 0.08, 3);
  const yCont = offsetY + ALTO_MITAD * 0.82;

  const textoId = escapar(p.id);
  const fuenteId = fuenteQueEntra(textoId, ALTO_MITAD * 0.06, 2.2);
  const yId = offsetY + ALTO_MITAD * 0.92;

  return (
    barras(p.id, offsetY, barY, barAlto) +
    `<text x="${ANCHO / 2}" y="${yProd.toFixed(3)}" font-family="${FUENTE}" font-weight="800" ` +
    `font-size="${fuenteProd.toFixed(2)}" text-anchor="middle" fill="#000">${textoProd}</text>` +
    (textoCont
      ? `<text x="${ANCHO / 2}" y="${yCont.toFixed(3)}" font-family="${FUENTE}" font-weight="700" ` +
        `font-size="${fuenteCont.toFixed(2)}" text-anchor="middle" fill="#000">${textoCont}</text>`
      : '') +
    `<text x="${ANCHO / 2}" y="${yId.toFixed(3)}" font-family="${FUENTE_MONO}" ` +
    `font-size="${fuenteId.toFixed(2)}" text-anchor="middle" fill="#000">${textoId}</text>`
  );
}

/**
 * El dibujo de una hoja de 100×150: el pallet de arriba, el de abajo si hay, y
 * la línea de corte punteada entre los dos. Con un solo pallet el espacio de
 * abajo queda en blanco; la línea se deja igual, porque sigue marcando dónde
 * cortar el rollo para la próxima hoja.
 */
export function svgEtiquetaPallets(par: [PalletEtiqueta, PalletEtiqueta | null]): string {
  const [arriba, abajo] = par;
  const corte =
    `<line x1="3" y1="${ALTO_MITAD}" x2="${ANCHO - 3}" y2="${ALTO_MITAD}" stroke="#000" stroke-width="0.3" stroke-dasharray="2.5,1.5"/>`;

  return (
    `<svg viewBox="0 0 ${ANCHO} ${ALTO}" xmlns="http://www.w3.org/2000/svg">` +
    `<rect x="0" y="0" width="${ANCHO}" height="${ALTO}" fill="#fff"/>` +
    mitad(arriba, 0) +
    (abajo ? mitad(abajo, ALTO_MITAD) : '') +
    corte +
    `</svg>`
  );
}
